import type { FilterSchema } from '@/shared/components/filter/validation'
import type { ChartConfig } from '@/shared/components/ui/chart'
import type { WidgetPeriods } from '../types'

import { formatMetricValue, useMoneyMetrics } from '@/pages/dashboard/lib/money'
import { isNil, last, map, pipe, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'
import { Area, AreaChart, XAxis } from 'recharts'

import {
	DateAggregation,
	filterPricingAndBusinessMetrics,
	sanitizeResponseBySelectedMetrics,
	useBIAnalyticQuery,
	useMetricsQuery,
} from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from '@/shared/components/ui/chart'
import { intlFormat } from '@/shared/tools/dates'
import { NumberFormats } from '@/shared/tools/locale'

import MetricTooltipRow from '../../MetricTooltipRow'
import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetErrorState from '../../WidgetErrorState'
import WidgetLoadingState from '../../WidgetLoadingState'

const selectMainMatrics = pipe([
	filterPricingAndBusinessMetrics,
	map(prop('name')),
])

export default function MetricPerfomanceWidget({
	commonFilters,
	commonPeriods,
	params,
}: {
	commonPeriods: WidgetPeriods
	commonFilters: FilterSchema[]
	params: {
		metric: string
		format: NumberFormats
	}
}) {
	const { t } = useTranslation()

	const { data: metrics, ...metricsQuery } = useMetricsQuery<string[]>({
		placeholderData: [],
		select: selectMainMatrics,
	})
	const currency = useSelectedCurrency()
	const moneyMetrics = useMoneyMetrics()
	const commonQueryParams = {
		currency,
		label: 'MetricPerfomance',
		...(commonPeriods.main || {
			start_date: null,
			end_date: null,
		}),
		metrics: metrics as string[],
		dimensions: [],
		filters: [...commonFilters],
	}
	const biQuerySummary = useBIAnalyticQuery(
		{
			...commonQueryParams,
			date_aggregation: null,
		},
		{
			enabled: !!metrics?.length,
			select: (res) =>
				sanitizeResponseBySelectedMetrics(
					res!,
					[params.metric as string],
					false
				),
		}
	)
	const biQuery = useBIAnalyticQuery(
		{
			...commonQueryParams,
			date_aggregation: DateAggregation.DateKey,
		},
		{
			enabled: !!metrics?.length,
			select: (res) =>
				sanitizeResponseBySelectedMetrics(res!, [params.metric!], true),
		}
	)
	const value = last(last(biQuerySummary?.data?.data))
	const chartConfig = {
		metric: {
			label: t(params.metric as string),
		},
	} satisfies ChartConfig
	const chartData =
		biQuery?.data?.data.map((item) => ({
			date: item[0],
			metric: item[1],
		})) || []

	return (
		<Widget className='relative flex flex-auto flex-col overflow-hidden pb-0'>
			<WidgetLoadingState
				isTinted={
					metricsQuery.isLoading ||
					biQuerySummary.isFetching ||
					biQuery.isFetching
				}
			/>
			<WidgetErrorState
				isTinted={
					metricsQuery.isError || biQuerySummary.isError || biQuery.isError
				}
			/>
			<WidgetHeader
				title={t(params.metric as string)}
				subtitle={t('app.based_on_last_integration')}
			/>
			<WidgetContent className='mt-auto truncate px-0'>
				<div className='mt-auto w-full'>
					<span className='data-text px-4 text-success'>
						{isNil(value)
							? '-'
							: formatMetricValue(
									value,
									params.metric,
									currency,
									moneyMetrics,
									params.format as NumberFormats
								)}
					</span>
					<ChartContainer config={chartConfig} className='aspect-[1/0.35]'>
						<AreaChart
							accessibilityLayer
							data={chartData}
							margin={{
								left: 0,
								right: 0,
							}}
						>
							<ChartTooltip
								cursor={false}
								content={
									<ChartTooltipContent
										labelFormatter={(value) =>
											typeof value === 'string' || typeof value === 'number'
												? intlFormat(new Date(value))
												: ''
										}
										indicator='dot'
										formatter={(v, _name, item) => (
											<MetricTooltipRow
												color={item.color}
												label={chartConfig.metric.label}
												value={formatMetricValue(
													Number(v),
													params.metric,
													currency,
													moneyMetrics,
													NumberFormats.Standart
												)}
											/>
										)}
									/>
								}
							/>
							<XAxis hide dataKey='date' />
							<Area
								dataKey='metric'
								type='natural'
								fill='var(--color-green-500)'
								fillOpacity={0.4}
								stroke='var(--color-green-500)'
							/>
						</AreaChart>
					</ChartContainer>
				</div>
			</WidgetContent>
		</Widget>
	)
}
